import { Box, CircularProgress, Grid, Paper, Typography, Alert } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';

import EstimatedTaxesSectionTable from './components/estimated-taxes-section-table';
import Header from './components/header';
import PaymentSectionTable from './components/payment-section-table';
import { UseFormReturn } from 'react-hook-form';
import { accountingProjectedType, ProjectedPaymentsForm } from './services';
import { getProjectedPaymentsError } from './services/validation';
import { useTranslation } from 'react-i18next';
import { zodResolver } from '@hookform/resolvers/zod';
import AddLedgerEntry from 'components/add-entry-modal/add-entry-modal';
import { createAddEntrySchema } from 'components/add-entry-modal/services/validate-entry';
import { getApiErrorMessage } from 'utils/api-error';
import { AccountingType } from 'v2-types/order';
import { useIsMobile } from './components/use-projected-payments-viewport';

const addEntrySchema = createAddEntrySchema();

const useStyles = makeStyles((theme: any) => ({
  rootContainer: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(1) },
  },
  mainContainer: {
    padding: theme.spacing(3),
    minWidth: 0,
    [theme.breakpoints.down('sm')]: { padding: theme.spacing(1.5) },
  },
  section: { minWidth: 0 },
  header: {
    color: theme.palette.tab.offselected,
    fontWeight: 500,
  },
  fieldset: {
    border: 0,
    margin: 0,
    padding: 0,
    minWidth: 0,
  },
  alert: { marginBottom: theme.spacing(2) },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
}));

type ProjectedPaymentsSceneProps = {
  methods: UseFormReturn<ProjectedPaymentsForm>;
  loadingSave?: boolean;
  isLoadingData?: boolean;
  loadError?: unknown;
  handleSave: (data: ProjectedPaymentsForm) => void;
  handleEditMortgage: () => void;
  handleEditEstimated: () => void;
  handleEditPrincipalInterest: () => void;
  currentEntry: accountingProjectedType | undefined;
  setCurrentEntry: React.Dispatch<React.SetStateAction<accountingProjectedType | undefined>>;
  estimatedCalculatedValue: number;
  mortgageCalculatedValue: number;
  principalInterestCalculatedValue: number;
  isLoadingMortgageInsurance: boolean;
};

const ProjectedPaymentsScene = ({
  methods,
  loadingSave,
  isLoadingData = false,
  loadError,
  handleSave,
  handleEditEstimated,
  handleEditMortgage,
  handleEditPrincipalInterest,
  currentEntry,
  setCurrentEntry,
  estimatedCalculatedValue,
  mortgageCalculatedValue,
  principalInterestCalculatedValue,
  isLoadingMortgageInsurance,
}: ProjectedPaymentsSceneProps) => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const isSaving = !!loadingSave || !!isLoadingMortgageInsurance;
  const validationError = getProjectedPaymentsError(methods.watch(), t);
  const isValid = !isLoadingData && !loadError && !validationError;
  return (
    <form onSubmit={methods.handleSubmit((data) => handleSave(data))}>
      <Box className={classes.rootContainer}>
        <Paper>
          <Grid container direction="column">
            <Grid item xs={12}>
              <Header t={t} isValid={isValid} loading={isSaving} />
            </Grid>

            <Grid item xs={12} className={classes.mainContainer}>
              {loadError && (
                <Alert severity="error" className={classes.alert}>
                  {getApiErrorMessage(loadError, t)}
                </Alert>
              )}

              {!loadError && isLoadingData && (
                <Box className={classes.loading}>
                  <CircularProgress />
                </Box>
              )}

              {!loadError && !isLoadingData && (
                <>
                  {validationError && (
                    <Alert severity="error" className={classes.alert}>
                      {validationError}
                    </Alert>
                  )}
                  <fieldset disabled={isSaving} className={classes.fieldset}>
                    <Grid container direction="row" spacing={isMobile ? 2 : 5}>
                      <Grid item xs={12}>
                        <Typography variant="h6" className={classes.header}>
                          {t('projectedPayments:payments')}
                        </Typography>
                      </Grid>

                      <Grid item xs={12} lg={6} className={classes.section}>
                        <PaymentSectionTable
                          methods={methods}
                          onEditEstimated={handleEditEstimated}
                          onEditMortgage={handleEditMortgage}
                          onEditPrincipalInterest={handleEditPrincipalInterest}
                          calculatedMortgageValue={mortgageCalculatedValue}
                          calculatedEstimatedEscrow={estimatedCalculatedValue}
                          calculatedPrincipalInterestValue={principalInterestCalculatedValue}
                          isMobile={isMobile}
                          isSaving={isSaving}
                        />
                      </Grid>

                      <Grid item xs={12} lg={6} className={classes.section}>
                        <EstimatedTaxesSectionTable methods={methods} isMobile={isMobile} isSaving={isSaving} />
                      </Grid>
                    </Grid>
                  </fieldset>
                </>
              )}
            </Grid>
          </Grid>
        </Paper>
        {currentEntry && (
          <AddLedgerEntry
            open={!!currentEntry}
            handleClose={() => {
              setCurrentEntry(undefined);
            }}
            prevData={currentEntry as AccountingType}
            kindAllowedEntry={['manual']}
            codeAllowedEntry={['charge']}
            resolver={zodResolver(addEntrySchema)}
            disableFields={{}}
            hideFields={{
              code: true,
              letter: true,
              kind: true,
              number: true,
              amount: true,
              annuality: true,
              entryDate: true,
              months: true,
              months_advanced: true,
            }}
            isEditingEntry
          />
        )}
      </Box>
    </form>
  );
};

export default ProjectedPaymentsScene;
